import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, CircularProgress, Container, Typography} from '@mui/material';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import config from '../common/config';
import {useEntryImportMutation} from '../services/records';
import {useAppSelector} from '../hooks';
import {entriesAddMany} from './contentSlice';
import { useDispatch } from 'react-redux';
import { selectIsAuthenticated } from './authSlice';
import { localStorageSetIsIntroDialogCompleted } from '../localStorage';
import { Entry, Repository, PropertyDefinition } from '../types';
import { PROPERTY_DEFINITION_TYPES } from '../constants';

const apiBaseUrl = () => config.remoteApiAccountsUrl.replace(/\/?$/, '');

const fetchSharedProperties = async (
	repositoryId: string,
	key: string,
): Promise<{entries: Entry[], repository: Repository, accountName: string}> => {
	const q = new URLSearchParams({
		repositoryId,
		key,
	});
	const res = await fetch(
		`${apiBaseUrl()}/repositories/getProperties?${q.toString()}`,
		{method: 'GET'},
	);
	if (!res.ok) {
		const message =
			res.status === 401
				? 'Invalid or expired share key.'
				: res.status === 404
					? 'Repository not found.'
					: `Could not load shared schema (${res.status}).`;
		throw new Error(message);
	}
	return res.json();
};

const ReplicateRepositoryView = () => {
	const {repositoryId = ''} = useParams<{repositoryId: string}>();
	const [searchParams] = useSearchParams();
	const key = searchParams.get('key') ?? '';
	const navigate = useNavigate();
	const [importEntries] = useEntryImportMutation();

	const [payload, setPayload] = useState<{entries: Entry[], repository: Repository, accountName: string} | null>(null);
	const payloadEntries = useMemo(() => payload ? [payload.repository, ...payload.entries] : [], [payload]);
	const payloadRepository = useMemo(() => payload?.repository as Repository ?? null, [payload]);
	const payloadAccountName = useMemo(() => payload?.accountName ?? '', [payload]);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const dispatch = useDispatch();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);

	useEffect(() => {
		let cancelled = false;
		if (!repositoryId || !key) {
			setLoading(false);
			setLoadError(
				!repositoryId
					? 'Missing repository in the link.'
					: 'Missing key in the link. Open the full share URL from the owner.',
			);
			return;
		}
		setLoading(true);
		setLoadError(null);
		fetchSharedProperties(repositoryId, key)
			.then((data) => {
				const {entries, repository, accountName} = data;
				if (!cancelled) {
					setPayload({entries, repository, accountName});
				}
			})
			.catch((e: Error) => {
				if (!cancelled) {
					setLoadError(e.message ?? 'Failed to load.');
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [repositoryId, key]);

	const handleReplicate = useCallback(async () => {
		if (!payloadEntries.length) {
			return;
		}
		if (isAuthenticated) {
			// this includes an optimistic local add
			await importEntries(payloadEntries as any).unwrap();
		} else {
			localStorageSetIsIntroDialogCompleted(true);
			dispatch(entriesAddMany(payloadEntries as any));
		}
		const newRepositoryId = payloadRepository?.id;
		if (newRepositoryId) {
			navigate(`/collection/${newRepositoryId}`);
		}
	}, [payloadEntries, importEntries, navigate, dispatch, isAuthenticated]);

	const handleReject = useCallback(() => {
		navigate('/');
	}, [navigate]);

	const propertyDefinitions = useMemo(() => {
		return payloadEntries.filter((entry) => PROPERTY_DEFINITION_TYPES.includes(entry.type as any)) as PropertyDefinition[];
	}, [payloadEntries]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: 'calc(100dvh - 80px)',
				px: 2,
				py: 4,
			}}
		>
			{payloadRepository?.title && <Helmet>
				<title>Replicate: {payloadRepository.title} — {config.appName}</title>
			</Helmet>}
			<Container maxWidth='sm'>
				<Typography variant='h3' component='h1' gutterBottom align='center' sx={{maxWidth: 520, mx: 'auto'}}>
					{repositoryId && key
						? `${payloadAccountName ? payloadAccountName : 'Someone'} shared a schema with you`
						: 'Shared collection link'}
				</Typography>
				{loading && (
					<Box display='flex' justifyContent='center' py={4}>
						<CircularProgress />
					</Box>
				)}
				{!loading && loadError && (
					<>
						<Typography color='error' align='center' paragraph>
							{loadError}
						</Typography>
						<Box display='flex' justifyContent='center' mt={6}>
							<Button variant='outlined' size='large' onClick={handleReject} sx={{minWidth: {sm: 220}, py: 2}}>
								Return to workspace
							</Button>
						</Box>
					</>
				)}
				{!loading && !loadError && payload && (
					<>
						<Typography variant='body1' paragraph align='center'>
							<strong>{payloadRepository.title}</strong>
							{' — '}
							{propertyDefinitions.length} field
							{propertyDefinitions.length === 1 ? '' : 's'} in this schema.
						</Typography>
						<Typography variant='body2' color='text.secondary' paragraph align='center'>
							Add a new collection to your workspace using this schema.
						</Typography>
						<Box
							display='flex'
							flexDirection={{xs: 'column', sm: 'row'}}
							justifyContent='center'
							alignItems='stretch'
							gap={2}
							mt={2}
						>
							<Button
								variant='contained'
								size='large'
								onClick={handleReplicate}
								sx={{minWidth: {sm: 220}, py: 2}}
							>
								Yes, proceed
							</Button>
							<Button
								variant='outlined'
								size='large'
								onClick={handleReject}
								sx={{minWidth: {sm: 220}, py: 2}}
							>
								Reject
							</Button>
						</Box>
					</>
				)}
			</Container>
		</Box>
	);
};

export default ReplicateRepositoryView;
